(() => {
  "use strict";

  function create(hooks) {
    const doc = hooks.document || document;
    let active = null, cache = null, generation = 0;
    const owner = () => [...(hooks.owner?.() || [])];
    const canWrite = () => hooks.canWrite ? Boolean(hooks.canWrite()) : true;
    const sameOwner = (left, right) => left.length === right.length && left.every((value, index) => value === right[index]);
    const node = (tag, className, text) => {
      const element = doc.createElement(tag);
      if (className) element.className = className;
      if (text != null) element.textContent = text;
      return element;
    };
    const button = (text, className, action) => {
      const element = node("button", className, text); element.type = "button";
      element.addEventListener("click", action); return element;
    };
    const current = panel => active === panel && panel.generation === generation
      && panel.dialog.open && cache === panel.cache && sameOwner(panel.cache.owner, owner());
    const entryCurrent = (panel, entry) => current(panel) && cache.entries.get(entry.item.user.id) === entry;
    const personName = user => user.display_name || user.handle || "Member";
    // A 4xx other than a timeout is the server's definitive refusal (for example the follow rate
    // limit). Keep it to explain a request that is still waiting; a lost answer has no reason.
    const refusal = error => {
      const status = Number(error?.status);
      const message = typeof error?.message === "string" ? error.message.trim() : "";
      return status >= 400 && status < 500 && status !== 408 && message ? message : "";
    };

    function close({ navigation = false, reason = "dismiss" } = {}) {
      const panel = active;
      if (!panel) return;
      active = null; generation++;
      panel.cache.scroll = panel.list.scrollTop;
      panel.controller?.abort();
      for (const controller of panel.recoveries.values()) controller.abort();
      panel.recoveries.clear();
      if (panel.dialog.open) {
        if (hooks.closeDialog) hooks.closeDialog(panel.dialog);
        else panel.dialog.close();
      }
      panel.dialog.remove();
      if (!navigation) hooks.onClose?.(reason);
    }

    function syncIdentity() {
      if (cache && !sameOwner(cache.owner, owner())) {
        close({ reason: "identity" }); cache = null;
      }
      if (active) paint(active);
    }

    function syncCapabilities() { if (active) paint(active); }

    function validItem(item) {
      return item && typeof item.id === "string" && Boolean(item.id)
        && typeof item.user?.id === "string" && Boolean(item.user.id)
        && typeof item.user.handle === "string" && Boolean(item.user.handle);
    }

    function pageData(result) {
      if (!result || !Array.isArray(result.items) || !result.items.every(validItem)
          || !(result.next_cursor === null || (typeof result.next_cursor === "string" && result.next_cursor))) {
        throw new Error("The request list could not be confirmed. Try again.");
      }
      const ids = new Set();
      for (const item of result.items) {
        if (ids.has(item.user.id)) throw new Error("The request list could not be confirmed. Try again.");
        ids.add(item.user.id);
      }
      return result;
    }

    function install(items, nextCursor, append = false) {
      const entries = append ? new Map(cache.entries) : new Map();
      const order = append ? [...cache.order] : [];
      for (const item of items) {
        const old = cache.entries.get(item.user.id);
        const entry = old && (old.item.id === item.id || old.operation || old.uncertain)
          ? old : { item, operation: null, uncertain: false, error: "" };
        entry.item = item;
        entries.set(item.user.id, entry);
        if (!order.includes(item.user.id)) order.push(item.user.id);
      }
      // A loaded window can move when new requests arrive. An unresolved decision
      // stays visible until its requester-specific read confirms current status.
      for (const [id, entry] of cache.entries) {
        if ((entry.operation || entry.uncertain) && !entries.has(id)) { entries.set(id, entry); order.push(id); }
      }
      cache.entries = entries; cache.order = order; cache.nextCursor = nextCursor;
      cache.loaded = true; cache.stale = false;
    }

    function makeRow(panel, entry) {
      const user = entry.item.user, row = node("li", "follow-requests-row");
      row.dataset.requesterId = user.id;
      const profile = button("", "follow-requests-profile", () => {
        if (!entryCurrent(panel, entry)) return;
        const selected = entry.item.user;
        close({ navigation: true }); hooks.openProfile?.(selected);
      });
      const avatar = node("span", "avatar follow-requests-avatar", personName(user).slice(0, 1));
      avatar.setAttribute("aria-hidden", "true"); hooks.styleAvatar?.(avatar, user);
      const identity = node("span", "follow-requests-identity");
      const name = node("strong", "", personName(user)), handle = node("small", "", `@${user.handle}`);
      identity.append(name, handle); profile.append(avatar, identity);
      const actions = node("div", "follow-requests-actions");
      const accept = button("Accept", "publish-button follow-requests-action", () => { void decide(panel, entry, "accept"); });
      const decline = button("Decline", "secondary-button follow-requests-action", () => { void decide(panel, entry, "decline"); });
      const check = button("Check status", "secondary-button follow-requests-action", () => { void recover(panel, entry); });
      const message = node("p", "follow-requests-row-status"); message.setAttribute("role", "status");
      actions.append(accept, decline, check); row.append(profile, actions, message);
      return { row, profile, name, handle, accept, decline, check, message, entry };
    }

    function paint(panel) {
      if (!current(panel)) return;
      const retained = new Set(cache.order);
      for (const [id, view] of panel.rows) if (!retained.has(id)) { view.row.remove(); panel.rows.delete(id); }
      for (const [index, id] of cache.order.entries()) {
        const entry = cache.entries.get(id), user = entry.item.user;
        let view = panel.rows.get(id);
        if (!view || view.entry !== entry) {
          view?.row.remove(); view = makeRow(panel, entry); panel.rows.set(id, view);
        }
        view.name.textContent = personName(user); view.handle.textContent = `@${user.handle}`;
        view.profile.setAttribute("aria-label", `View ${personName(user)}'s profile`);
        view.accept.setAttribute("aria-label", `Accept ${personName(user)}'s follow request`);
        view.decline.setAttribute("aria-label", `Decline ${personName(user)}'s follow request`);
        view.check.setAttribute("aria-label", `Check status of ${personName(user)}'s follow request`);
        const recovering = panel.recoveries.has(id);
        const disabled = !canWrite() || cache.stale || entry.uncertain || recovering;
        // Keep focus while a decision is pending; decide() still rejects repeated input.
        const unavailable = disabled || Boolean(entry.operation);
        view.accept.disabled = disabled; view.decline.disabled = disabled;
        view.accept.setAttribute("aria-disabled", String(unavailable)); view.decline.setAttribute("aria-disabled", String(unavailable));
        view.check.hidden = !entry.uncertain; view.check.disabled = recovering || Boolean(entry.operation) || panel.reading;
        view.message.textContent = entry.operation ? (entry.operation.action === "accept" ? "Accepting…" : "Declining…")
          : recovering ? "Checking request…" : entry.error || (!canWrite() ? "Follow request decisions are currently unavailable." : "");
        view.message.hidden = !view.message.textContent;
        view.row.setAttribute("aria-busy", String(Boolean(entry.operation) || recovering));
        if (panel.list.children[index] !== view.row) panel.list.insertBefore(view.row, panel.list.children[index] || null);
      }
      panel.list.setAttribute("aria-busy", String(panel.reading));
      panel.status.textContent = panel.reading ? (cache.loaded ? "Checking requests…" : "Loading requests…")
        : cache.loaded && !cache.order.length && !cache.nextCursor ? "No follow requests"
          : cache.loaded && !cache.order.length && cache.nextCursor ? "More requests are available." : "";
      panel.status.hidden = !panel.status.textContent;
      panel.error.textContent = panel.readError; panel.error.hidden = !panel.readError;
      panel.retry.hidden = !panel.readError; panel.retry.disabled = panel.reading;
      panel.more.hidden = !cache.nextCursor; panel.more.disabled = panel.reading || cache.stale;
      panel.more.textContent = panel.reading && panel.mode === "more" ? "Loading…" : "Load more";
      panel.refresh.disabled = panel.reading;
    }

    async function read(panel, more = false) {
      if (!current(panel) || panel.reading || (more && (!cache.nextCursor || cache.stale))) return;
      const controller = panel.controller = new AbortController();
      const readRevision = cache.revision;
      panel.reading = true; panel.readError = ""; panel.mode = more ? "more" : "window";
      if (!more) cache.stale = true;
      paint(panel);
      const stillCurrent = () => current(panel) && panel.controller === controller && !controller.signal.aborted;
      try {
        const items = [], seenCursors = more ? new Set(cache.cursors) : new Set();
        let cursor = more ? cache.nextCursor : null, nextCursor = null, pages = 0;
        const targetPages = more ? 1 : Math.max(1, cache.pages);
        do {
          const params = new URLSearchParams({ limit: "30" });
          if (cursor) params.set("cursor", cursor);
          const result = pageData(await hooks.api(`/api/follow-requests?${params}`, { signal: controller.signal }));
          if (!stillCurrent()) return;
          items.push(...result.items); pages++; nextCursor = result.next_cursor;
          if (nextCursor && (nextCursor === cursor || seenCursors.has(nextCursor))) throw new Error("The next request page could not be confirmed. Try again.");
          if (cursor) seenCursors.add(cursor);
          cursor = nextCursor;
        } while (cursor && pages < targetPages);
        if (cache.revision !== readRevision) throw new Error("Requests changed while loading. Refresh to see the current list.");
        install(items, nextCursor, more);
        if (nextCursor) seenCursors.add(nextCursor);
        cache.cursors = seenCursors;
        cache.pages = more ? cache.pages + pages : pages;
      } catch (error) {
        if (!stillCurrent()) return;
        panel.readError = error?.message || "Follow requests could not load. Try again.";
      } finally {
        if (stillCurrent()) {
          panel.reading = false; panel.controller = null; paint(panel);
          if (!panel.readError) for (const entry of cache.entries.values()) {
            if (entry.uncertain && !entry.operation) void recover(panel, entry);
          }
        }
      }
    }

    function removeEntry(panel, entry, message) {
      if (!entryCurrent(panel, entry)) return;
      const id = entry.item.user.id, index = cache.order.indexOf(id);
      const focused = panel.rows.get(id)?.row.contains?.(doc.activeElement);
      cache.revision++;
      cache.entries.delete(id); cache.order = cache.order.filter(value => value !== id);
      paint(panel);
      if (focused) {
        const next = panel.rows.get(cache.order[Math.min(index, cache.order.length - 1)]);
        (next?.profile || (!panel.more.hidden ? panel.more : panel.refresh)).focus({ preventScroll: true });
      }
      hooks.toast?.(message);
    }

    async function recover(panel, entry, refused = "") {
      const id = entry.item.user.id;
      if (!entryCurrent(panel, entry) || entry.operation || panel.recoveries.has(id) || panel.reading) return;
      const controller = new AbortController(); panel.recoveries.set(id, controller); paint(panel);
      const stillCurrent = () => entryCurrent(panel, entry) && panel.recoveries.get(id) === controller && !controller.signal.aborted;
      try {
        const result = pageData(await hooks.api(`/api/follow-requests?requester_id=${encodeURIComponent(id)}`, { signal: controller.signal }));
        if (!stillCurrent()) return;
        if (result.next_cursor !== null || result.items.length > 1 || result.items.some(item => item.user.id !== id)) {
          throw new Error("The request status could not be confirmed.");
        }
        if (result.items.length) {
          cache.revision++;
          entry.item = result.items[0]; entry.uncertain = false;
          // Only the recovery that directly follows a refused decision may explain it.
          entry.error = refused ? `${refused} This request is still waiting.`
            : "This request is still waiting. Choose Accept or Decline to try again.";
        } else {
          let following = false;
          try {
            const profile = await hooks.api(`/api/profiles/${encodeURIComponent(entry.item.user.handle)}`, { signal: controller.signal });
            if (!stillCurrent()) return;
            following = profile?.user?.id === id && profile.relationship?.follows_you === true && profile.relationship?.blocked !== true;
          } catch (_) { if (!stillCurrent()) return; }
          removeEntry(panel, entry, following ? "Now following you" : "Request no longer available");
        }
      } catch (_) {
        if (stillCurrent()) { entry.uncertain = true; entry.error = "We couldn't confirm this request. Check its status before deciding again."; }
      } finally {
        if (panel.recoveries.get(id) === controller) panel.recoveries.delete(id);
        if (current(panel)) paint(panel);
      }
    }

    async function decide(panel, entry, action) {
      if (!entryCurrent(panel, entry) || !canWrite() || cache.stale || entry.operation || entry.uncertain || panel.recoveries.has(entry.item.user.id)) return;
      const operation = entry.operation = { action, requestId: entry.item.id }, operationCache = cache;
      cache.revision++;
      entry.error = ""; paint(panel);
      let refused = "";
      try {
        const result = await hooks.api(`/api/follow-requests/${encodeURIComponent(entry.item.user.id)}`, {
          method: "PUT", csrf: true, json: { action },
        });
        if (!entryCurrent(panel, entry) || entry.item.id !== operation.requestId) {
          if (cache === operationCache && sameOwner(cache.owner, owner())) entry.uncertain = true;
          return;
        }
        if (result?.accepted !== (action === "accept")) throw new Error("Decision receipt could not be confirmed.");
        removeEntry(panel, entry, action === "accept" ? "Request accepted" : "Request declined");
      } catch (error) {
        if (cache === operationCache && sameOwner(cache.owner, owner())) {
          entry.uncertain = true; entry.error = "Checking whether this request is still waiting…";
          refused = refusal(error);
        }
      } finally {
        if (entry.operation === operation) entry.operation = null;
        // A reopened inbox never paints an older write receipt; it reads current
        // pending/follower state under the new open generation before enabling actions.
        if (active && cache === operationCache && entryCurrent(active, entry)) {
          paint(active); if (entry.uncertain) void recover(active, entry, refused);
        }
      }
    }

    function open() {
      syncIdentity();
      const identity = owner();
      if (!identity[0]) return false;
      if (active && current(active)) return true;
      if (active) close({ navigation: true });
      if (!cache) cache = { owner: identity, entries: new Map(), order: [], nextCursor: null, pages: 0, loaded: false, stale: true, scroll: 0, revision: 0, cursors: new Set() };
      cache.stale = true;
      const dialog = node("dialog", "small-dialog follow-requests-dialog");
      dialog.dataset.noHistory = "true"; dialog.setAttribute("aria-label", "Follow requests");
      const panel = { dialog, cache, generation: ++generation, rows: new Map(), recoveries: new Map(),
        reading: false, controller: null, readError: "", mode: "window" };
      const header = node("header", "follow-requests-header"), title = node("h2", "", "Follow requests");
      panel.refresh = button("Refresh", "secondary-button follow-requests-action", () => { void read(panel); });
      const dismiss = button("Close", "secondary-button follow-requests-action", () => close());
      header.append(title, panel.refresh, dismiss);
      panel.status = node("p", "follow-requests-status"); panel.status.setAttribute("role", "status"); panel.status.setAttribute("aria-live", "polite");
      panel.error = node("p", "follow-requests-error"); panel.error.setAttribute("role", "alert");
      panel.retry = button("Retry", "secondary-button follow-requests-action", () => { void read(panel, panel.mode === "more"); });
      panel.list = node("ul", "follow-requests-list"); panel.list.setAttribute("aria-label", "People requesting to follow you");
      panel.more = button("Load more", "secondary-button follow-requests-action follow-requests-more", () => { void read(panel, true); });
      dialog.append(header, panel.status, panel.error, panel.retry, panel.list, panel.more);
      dialog.addEventListener("cancel", event => { event.preventDefault(); if (active === panel) close(); });
      dialog.addEventListener("close", () => { if (active === panel) close(); });
      doc.body.append(dialog); active = panel;
      if (hooks.openDialog) hooks.openDialog(dialog); else dialog.showModal();
      paint(panel); panel.list.scrollTop = cache.scroll;
      dismiss.focus({ preventScroll: true });
      void read(panel); return true;
    }

    return { open, close, syncIdentity, syncCapabilities };
  }

  globalThis.LouderrFollowRequests = { create };
  if (typeof module !== "undefined") module.exports = { create };
})();
